/**
 * Reporte: Deuda por empresa (NUEVO M13) — Fase 7.3 (reporte 3 de 7)
 *
 * Ranking de empresas con saldo > 0, ordenado por monto adeudado DESC.
 * Incluye antigüedad del cupón vencido más viejo para priorizar gestión
 * de cobranza.
 *
 * Reutiliza `vw_cuenta_corriente_resumen` (decisión #030).
 */

import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Card,
  Button,
  Alert,
  Chip,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

import { useCuentaCorrienteResumen } from '../../hooks/useCuentaCorriente';
import { formatCurrency, formatDate, formatCuit } from '../../utils/format';
import {
  type CuentaCorrienteResumen,
  getEstadoCtaCteLabel,
  getEstadoCtaCteColor,
} from '../../types/cuentaCorriente';

import {
  createReportePdf,
  addReporteTable,
  addReporteFooter,
  fmtCurrency,
  fmtDate,
  fmtText,
  type ColumnaPdf,
} from '../../utils/reportes/pdf';
import { generarReporteExcel, type ColumnaExcel } from '../../utils/reportes/excel';
import { triggerBlobDownload, buildReporteFilename } from '../../utils/reportes/download';

// ── Tipos auxiliares ──────────────────────────────────────────────────────────

interface DeudorRow extends CuentaCorrienteResumen {
  ranking: number;
  dias_mora_max: number;
}

function diasDesde(fechaIso: string | null): number {
  if (!fechaIso) return 0;
  const venc = new Date(fechaIso);
  const hoy = new Date();
  return Math.max(0, Math.floor((hoy.getTime() - venc.getTime()) / (1000 * 60 * 60 * 24)));
}

// ── Componente ────────────────────────────────────────────────────────────────

type Orden = 'monto' | 'antiguedad';

export default function DeudaPorEmpresaReporte() {
  const { data: resumen = [], isLoading, isError } = useCuentaCorrienteResumen();

  const [orden, setOrden] = useState<Orden>('monto');

  const deudores: DeudorRow[] = useMemo(() => {
    const soloDeudores = resumen.filter((r) => Number(r.saldo_total) > 0);
    const conMora = soloDeudores.map((r) => ({
      ...r,
      ranking: 0,
      dias_mora_max: diasDesde(r.vencimiento_mas_antiguo),
    }));
    conMora.sort((a, b) => {
      if (orden === 'monto') return Number(b.saldo_total) - Number(a.saldo_total);
      return b.dias_mora_max - a.dias_mora_max;
    });
    return conMora.map((r, idx) => ({ ...r, ranking: idx + 1 }));
  }, [resumen, orden]);

  const totales = useMemo(
    () => ({
      cantidadEmpresas: deudores.length,
      totalDeuda: deudores.reduce((s, r) => s + Number(r.saldo_total), 0),
      cantVencidos: deudores.reduce((s, r) => s + r.cant_vencidos, 0),
      promedioPorEmpresa:
        deudores.length > 0
          ? deudores.reduce((s, r) => s + Number(r.saldo_total), 0) / deudores.length
          : 0,
    }),
    [deudores],
  );

  const subtitulo = `Ordenado por ${orden === 'monto' ? 'monto adeudado' : 'antigüedad de deuda'}`;

  // ── Export PDF ─────────────────────────────────────────────────────────
  const handleExportPdf = () => {
    const doc = createReportePdf({
      titulo: 'Deuda por empresa',
      subtitulo,
      orientacion: 'landscape',
    });

    const columnas: ColumnaPdf[] = [
      { header: '#', align: 'right', format: fmtText },
      { header: 'Empresa', format: fmtText },
      { header: 'CUIT', align: 'center', format: fmtText },
      { header: 'Cupones vencidos', align: 'right', format: fmtText },
      { header: 'Venc. más antiguo', align: 'center', format: fmtDate },
      { header: 'Días mora', align: 'right', format: fmtText },
      { header: 'Saldo', align: 'right', format: fmtCurrency },
      { header: 'Estado', align: 'center', format: (v) => getEstadoCtaCteLabel(String(v)) },
    ];

    const body: unknown[][] = deudores.map((d) => [
      d.ranking,
      d.empresa_razon_social,
      formatCuit(d.empresa_cuit),
      d.cant_vencidos,
      d.vencimiento_mas_antiguo,
      d.dias_mora_max,
      d.saldo_total,
      d.estado_general,
    ]);

    body.push([
      '',
      `TOTAL (${totales.cantidadEmpresas} empresas)`,
      '',
      totales.cantVencidos,
      '',
      '',
      totales.totalDeuda,
      '',
    ]);

    addReporteTable(doc, columnas, body, {
      titulo: 'Deuda por empresa',
      subtitulo,
    });
    addReporteFooter(doc);

    const blob = doc.output('blob');
    triggerBlobDownload(blob, buildReporteFilename('deuda-por-empresa', 'pdf'));
  };

  // ── Export Excel ───────────────────────────────────────────────────────
  const handleExportExcel = async () => {
    const columnas: ColumnaExcel[] = [
      { header: 'Ranking', key: 'ranking', width: 8, format: 'integer' },
      { header: 'Empresa', key: 'empresa', width: 35, format: 'text' },
      { header: 'CUIT', key: 'cuit', width: 14, format: 'text' },
      { header: 'Cupones totales', key: 'totales', width: 12, format: 'integer' },
      { header: 'Pendientes', key: 'pendientes', width: 12, format: 'integer' },
      { header: 'Vencidos', key: 'vencidos', width: 10, format: 'integer' },
      { header: 'Venc. más antiguo', key: 'vencMasViejo', width: 16, format: 'date' },
      { header: 'Días mora', key: 'diasMora', width: 10, format: 'integer' },
      { header: 'Emitido', key: 'emitido', width: 14, format: 'currency' },
      { header: 'Pagado', key: 'pagado', width: 14, format: 'currency' },
      { header: 'Saldo', key: 'saldo', width: 14, format: 'currency' },
      { header: 'Estado', key: 'estado', width: 14, format: 'text' },
    ];

    const rows = deudores.map((d) => ({
      ranking: d.ranking,
      empresa: d.empresa_razon_social,
      cuit: formatCuit(d.empresa_cuit),
      totales: d.cant_cupones,
      pendientes: d.cant_pendientes,
      vencidos: d.cant_vencidos,
      vencMasViejo: d.vencimiento_mas_antiguo ? new Date(d.vencimiento_mas_antiguo) : null,
      diasMora: d.dias_mora_max,
      emitido: d.monto_emitido_total,
      pagado: d.monto_pagado_total,
      saldo: d.saldo_total,
      estado: getEstadoCtaCteLabel(d.estado_general),
    }));

    const blob = await generarReporteExcel({
      titulo: 'Deuda por empresa',
      subtitulo,
      nombreHoja: 'Deuda',
      columnas,
      rows,
    });
    triggerBlobDownload(blob, buildReporteFilename('deuda-por-empresa', 'xlsx'));
  };

  // ── DataGrid columns ────────────────────────────────────────────────────
  const columns: GridColDef<DeudorRow>[] = [
    {
      field: 'ranking',
      headerName: '#',
      width: 50,
      align: 'right',
      headerAlign: 'right',
    },
    { field: 'empresa_razon_social', headerName: 'Empresa', flex: 2, minWidth: 220 },
    {
      field: 'empresa_cuit',
      headerName: 'CUIT',
      width: 130,
      renderCell: (p: GridRenderCellParams<DeudorRow>) => formatCuit(p.row.empresa_cuit),
    },
    {
      field: 'cant_vencidos',
      headerName: 'Vencidos',
      width: 100,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'vencimiento_mas_antiguo',
      headerName: 'Venc. más antiguo',
      width: 150,
      renderCell: (p: GridRenderCellParams<DeudorRow>) =>
        p.row.vencimiento_mas_antiguo ? formatDate(p.row.vencimiento_mas_antiguo) : '-',
    },
    {
      field: 'dias_mora_max',
      headerName: 'Días mora',
      width: 100,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams<DeudorRow>) => (
        <Typography
          variant="body2"
          color={p.row.dias_mora_max > 30 ? 'error' : 'inherit'}
          fontWeight={p.row.dias_mora_max > 60 ? 600 : 400}
        >
          {p.row.dias_mora_max}
        </Typography>
      ),
    },
    {
      field: 'saldo_total',
      headerName: 'Saldo',
      width: 150,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams<DeudorRow>) => (
        <Typography variant="body2" fontWeight={700} color="warning.main">
          {formatCurrency(p.row.saldo_total)}
        </Typography>
      ),
    },
    {
      field: 'estado_general',
      headerName: 'Estado',
      width: 130,
      renderCell: (p: GridRenderCellParams<DeudorRow>) => (
        <Chip
          label={getEstadoCtaCteLabel(p.row.estado_general)}
          color={getEstadoCtaCteColor(p.row.estado_general)}
          size="small"
        />
      ),
    },
  ];

  const sinDeudores = !isLoading && !isError && deudores.length === 0;

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        component={RouterLink}
        to="/reportes"
        sx={{ mb: 2 }}
      >
        Volver a Reportes
      </Button>

      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <TrendingDownIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h5" fontWeight={700}>
              Deuda por empresa
            </Typography>
            <Chip label="Nuevo" size="small" color="success" />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Ranking de empresas con saldo activo, ordenado por monto o
            antigüedad.
          </Typography>
        </Box>
      </Stack>

      {/* Filtro: orden */}
      <Card sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Ordenar por:
          </Typography>
          <ToggleButtonGroup
            value={orden}
            exclusive
            onChange={(_, val) => val && setOrden(val)}
            size="small"
          >
            <ToggleButton value="monto">Monto adeudado</ToggleButton>
            <ToggleButton value="antiguedad">Antigüedad de deuda</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Card>

      {/* Totales + Export */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        mb={2}
      >
        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" useFlexGap>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Empresas deudoras
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {totales.cantidadEmpresas}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Cupones vencidos
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {totales.cantVencidos}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Deuda total
            </Typography>
            <Typography variant="h6" fontWeight={700} color="warning.main">
              {formatCurrency(totales.totalDeuda)}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Promedio por empresa
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {formatCurrency(totales.promedioPorEmpresa)}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPdf}
            disabled={deudores.length === 0}
          >
            Descargar PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<TableViewIcon />}
            onClick={handleExportExcel}
            disabled={deudores.length === 0}
          >
            Descargar Excel
          </Button>
        </Stack>
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar la información de deuda.
        </Alert>
      )}
      {sinDeudores && (
        <Alert severity="success" sx={{ mb: 2 }}>
          ¡Sin empresas con deuda activa!
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={deudores}
          columns={columns}
          getRowId={(r) => r.empresa_id}
          loading={isLoading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{ border: 0 }}
        />
      </Card>
    </Box>
  );
}
